import { useState } from "react";
import { ResolvedWillOpenCard } from "../../helpers/willOpenCardHelper";

export interface PendingConfirmSelectionType {
  ptId: number;
  processId: number;
  isSelected: boolean;
  quantity: number;
  officeId?: number;
  serviceId: number | string;
  historyGridId?: number | string;
}

export interface UseWillOpenCardStateReturn {
  // Core WillOpenCard states
  pendingWillOpenCards: ResolvedWillOpenCard[];
  setPendingWillOpenCards: React.Dispatch<React.SetStateAction<ResolvedWillOpenCard[]>>;
  activeWillOpenCardIndex: number | null;
  setActiveWillOpenCardIndex: React.Dispatch<React.SetStateAction<number | null>>;
  pendingConfirmSelection: PendingConfirmSelectionType | null;
  setPendingConfirmSelection: React.Dispatch<React.SetStateAction<PendingConfirmSelectionType | null>>;
  willOpenCardFlowValid: boolean;
  setWillOpenCardFlowValid: React.Dispatch<React.SetStateAction<boolean>>;
  serviceAddInfo: Record<string, any>;
  setServiceAddInfo: React.Dispatch<React.SetStateAction<Record<string, any>>>;
  activeWillOpenCardServiceName: string | undefined;
  setActiveWillOpenCardServiceName: React.Dispatch<React.SetStateAction<string | undefined>>;
  openHeightInfoModal: boolean;
  setOpenHeightInfoModal: React.Dispatch<React.SetStateAction<boolean>>;

  // Modal open states
  openVkiModal: boolean;
  setOpenVkiModal: React.Dispatch<React.SetStateAction<boolean>>;
  openEgfrModal: boolean;
  setOpenEgfrModal: React.Dispatch<React.SetStateAction<boolean>>;
  openDirectionModal: boolean;
  setOpenDirectionModal: React.Dispatch<React.SetStateAction<boolean>>;
  openAskDoctorModal: boolean;
  setOpenAskDoctorModal: React.Dispatch<React.SetStateAction<boolean>>;
  openPregnantModal: boolean;
  setOpenPregnantModal: React.Dispatch<React.SetStateAction<boolean>>;
  openPregnantControlModal: boolean;
  setOpenPregnantControlModal: React.Dispatch<React.SetStateAction<boolean>>;
  openProcessDirectionModal: boolean;
  setOpenProcessDirectionModal: React.Dispatch<React.SetStateAction<boolean>>;
  openHivModal: boolean;
  setOpenHivModal: React.Dispatch<React.SetStateAction<boolean>>;
  openPacemakerModal: boolean;
  setOpenPacemakerModal: React.Dispatch<React.SetStateAction<boolean>>;
  openLabModal: boolean;
  setOpenLabModal: React.Dispatch<React.SetStateAction<boolean>>;
  openInvestigationModal: boolean;
  setOpenInvestigationModal: React.Dispatch<React.SetStateAction<boolean>>;
  openContrastModal: boolean;
  setOpenContrastModal: React.Dispatch<React.SetStateAction<boolean>>;
  openContrastRequestModal: boolean;
  setOpenContrastRequestModal: React.Dispatch<React.SetStateAction<boolean>>;
  openConsultationModal: boolean;
  setOpenConsultationModal: React.Dispatch<React.SetStateAction<boolean>>;
  openPathologyModal: boolean;
  setOpenPathologyModal: React.Dispatch<React.SetStateAction<boolean>>;
  pendingAddToPatient: boolean;
  setPendingAddToPatient: React.Dispatch<React.SetStateAction<boolean>>;
}

/**
 * Custom hook for managing WillOpenCard modal states.
 * This hook only manages state - handlers are defined in the parent component
 * to avoid circular dependencies with other functions like getServiceData and fetchPriceForService.
 */
export function useWillOpenCardState(): UseWillOpenCardStateReturn {
  // Core WillOpenCard states
  const [pendingWillOpenCards, setPendingWillOpenCards] = useState<ResolvedWillOpenCard[]>([]);
  const [activeWillOpenCardIndex, setActiveWillOpenCardIndex] = useState<number | null>(null);
  const [pendingConfirmSelection, setPendingConfirmSelection] = useState<PendingConfirmSelectionType | null>(null);
  const [willOpenCardFlowValid, setWillOpenCardFlowValid] = useState(true);
  const [serviceAddInfo, setServiceAddInfo] = useState<Record<string, any>>({});
  const [activeWillOpenCardServiceName, setActiveWillOpenCardServiceName] = useState<string | undefined>(undefined);
  const [openHeightInfoModal, setOpenHeightInfoModal] = useState(false);
  const [pendingAddToPatient, setPendingAddToPatient] = useState(false);

  // Modal open states
  const [openVkiModal, setOpenVkiModal] = useState(false);
  const [openEgfrModal, setOpenEgfrModal] = useState(false);
  const [openDirectionModal, setOpenDirectionModal] = useState(false);
  const [openAskDoctorModal, setOpenAskDoctorModal] = useState(false);
  const [openPregnantModal, setOpenPregnantModal] = useState(false);
  const [openPregnantControlModal, setOpenPregnantControlModal] = useState(false);
  const [openProcessDirectionModal, setOpenProcessDirectionModal] = useState(false);
  const [openHivModal, setOpenHivModal] = useState(false);
  const [openPacemakerModal, setOpenPacemakerModal] = useState(false);
  const [openLabModal, setOpenLabModal] = useState(false);
  const [openInvestigationModal, setOpenInvestigationModal] = useState(false);
  const [openContrastModal, setOpenContrastModal] = useState(false);
  const [openContrastRequestModal, setOpenContrastRequestModal] = useState(false);
  const [openConsultationModal, setOpenConsultationModal] = useState(false);
  const [openPathologyModal, setOpenPathologyModal] = useState(false);

  return {
    // Core WillOpenCard states
    pendingWillOpenCards,
    setPendingWillOpenCards,
    activeWillOpenCardIndex,
    setActiveWillOpenCardIndex,
    pendingConfirmSelection,
    setPendingConfirmSelection,
    willOpenCardFlowValid,
    setWillOpenCardFlowValid,
    serviceAddInfo,
    setServiceAddInfo,
    activeWillOpenCardServiceName,
    setActiveWillOpenCardServiceName,
    openHeightInfoModal,
    setOpenHeightInfoModal,

    // Modal open states
    openVkiModal,
    setOpenVkiModal,
    openEgfrModal,
    setOpenEgfrModal,
    openDirectionModal,
    setOpenDirectionModal,
    openAskDoctorModal,
    setOpenAskDoctorModal,
    openPregnantModal,
    setOpenPregnantModal,
    openPregnantControlModal,
    setOpenPregnantControlModal,
    openProcessDirectionModal,
    setOpenProcessDirectionModal,
    openHivModal,
    setOpenHivModal,
    openPacemakerModal,
    setOpenPacemakerModal,
    openLabModal,
    setOpenLabModal,
    openInvestigationModal,
    setOpenInvestigationModal,
    openContrastModal,
    setOpenContrastModal,
    openContrastRequestModal,
    setOpenContrastRequestModal,
    openConsultationModal,
    setOpenConsultationModal,
    openPathologyModal,
    setOpenPathologyModal,
    pendingAddToPatient,
    setPendingAddToPatient,
  };
}
